import { useEffect, useState } from "react";
import { useMe } from "@/data/user.ts";
import PageHeading from "@/components/ui/PageHeading.tsx";
import { useSEO } from "@/contexts/SeoContext.tsx";
import useGlobals from "@/utils/hooks/useGlobals.ts";
import InvoiceItem from "@/components/invoice/InvoiceItem.tsx";
import DashboardContentLayout from "@/layouts/DashboardContentLayout.tsx";
import NoItem from "@/components/NoItem.tsx";
import { useTranslation } from "react-i18next";
import List from "@edusogno/composites/list";
import ContractItem from "@/components/invoice/ContractItem";
import { Payment } from "@/types";

export default function Invoices() {
  const { me, isLoading } = useMe();

  const { setTitle, setDescription } = useSEO();
  const { setGlobalState } = useGlobals();
  const { t } = useTranslation();
  const [contracts, setContracts] = useState<Payment[]>();

  useEffect(() => {
    setGlobalState("backgroundClass", "bg-brand-background");
  }, []);

  useEffect(() => {
    if (!me) return;
    if (!me.item.user.payments) return;

    const uniqueProducts: { [productId: string]: Payment } = {};

    me.item.user.payments.forEach((payment) => {
      if (payment.product_id && !uniqueProducts[payment.product_id]) {
        uniqueProducts[payment.product_id] = payment;
      }
    });

    const uniquePayments = Object.values(uniqueProducts);
    setContracts(uniquePayments);
  }, [me, me?.item.user]);

  useEffect(() => {
    setTitle(
      `${me?.item.user.first_name} ${me?.item.user.last_name} | ${t(
        "invoices.header",
      )}`,
    );
    setDescription("Invoices Page");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [me]);

  return (
    <DashboardContentLayout loading={isLoading}>
      <PageHeading
        title={t("menu.invoices")}
        subtitle={t("invoices.sub_header")}
        subtitleClassName={"text-light"}
      />
      <List className={"overflow-y-auto max-h-[100%]"}>
        {me && me.item.user.payments.length > 0 ? (
          <div className={"flex flex-col gap-4 w-full mt-4"}>
            {contracts &&
              contracts.length > 0 &&
              contracts.map((item) => (
                <ContractItem
                  item={item}
                  key={`contract-${item.amount}-${item.data}`}
                />
              ))}
            {me.item.user.payments.map((item) => (
              <InvoiceItem
                item={item}
                key={`invoice-${item.amount}-${item.data}`}
                user={me.item.user}
              />
            ))}
          </div>
        ) : (
          <NoItem text={t("invoices.empty")} />
        )}
      </List>
    </DashboardContentLayout>
  );
}
